import BasisPragueLogo from "@/assets/images/basis_prague_logo.svg";
import AppStoreBadgeLink from "@/components/AppStoreBadgeLink";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { useAuth } from "@/lib/auth";
// import AppleSignInButton from '@/components/AppleSignInButton';
// import * as AppleAuthentication from 'expo-apple-authentication';
import DebugEnvironment from "@/components/DebugEnvironment";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { THEME } from "@/lib/theme";
import { TEST_IDS } from "@/testIdDefinition";
import { SymbolView } from "expo-symbols";
import * as WebBrowser from "expo-web-browser";
import { useColorScheme } from "nativewind";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Icon } from "../components/ui/icon";

export default function Login() {
    const { t } = useTranslation();
    const {
        signInWithMicrosoft,
        isSigningIn,
        canUnlockWithBiometric,
        biometricLabel,
        tryBiometricUnlock,
        biometricSymbol,
        biometricFallbackIcon,
    } = useAuth();
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [signInError, setSignInError] = useState<string | null>(null);
    const { colorScheme } = useColorScheme();
    const scheme = colorScheme ?? "light";

    useEffect(() => {
        if (Platform.OS === "web") {
            return;
        }

        WebBrowser.warmUpAsync().catch(() => undefined);
        return () => {
            WebBrowser.coolDownAsync().catch(() => undefined);
        };
    }, []);

    async function handleMicrosoftSignIn() {
        setSignInError(null);
        try {
            await signInWithMicrosoft();
        } catch (error) {
            setSignInError(
                t(`login.microsoft.errors.${(error as Error).message}`, {
                    defaultValue:
                        (error as Error).message || t("login.unknownError"),
                }),
            );
        }
    }

    async function handleBiometricUnlock() {
        setIsUnlocking(true);
        try {
            await tryBiometricUnlock();
        } catch (error) {
            console.error("Biometric unlock failed:", error);
        } finally {
            setIsUnlocking(false);
        }
    }

    return (
        <SafeAreaView className="flex-1 w-full bg-background">
            <DebugEnvironment />
            <View className="flex-1 px-4 web:mx-auto web-lg:w-[28em]">
                <View className="native:flex-1 items-center justify-center web:h-[200px] py-4">
                    <BasisPragueLogo
                        style={{
                            width: 200,
                            height: 87,
                            color: THEME[scheme].foreground,
                        }}
                        className="text-foreground"
                    />
                </View>
                <View className="pb-4 web:flex-grow web:justify-between">
                    <Card className="justify-around web:min-h-[150px]">
                        <CardHeader className="gap-y-[100px]">
                            {Platform.OS === "web" && (
                                <CardTitle className="text-center text-xl">
                                    {t("login.welcome")}
                                </CardTitle>
                            )}
                            <CardDescription className="text-center">
                                {t("login.continueWithSchool")}
                            </CardDescription>
                        </CardHeader>
                        {signInError && (
                            <Alert
                                icon={"circle-exclamation"}
                                variant="destructive"
                                className="w-auto ml-6 mr-6"
                            >
                                <AlertTitle>
                                    {t("login.signInFailed")}
                                </AlertTitle>
                                <AlertDescription>
                                    {signInError}
                                </AlertDescription>
                            </Alert>
                        )}
                        <CardContent className="flex-col gap-3">
                            {Platform.OS !== "web" &&
                                canUnlockWithBiometric && (
                                    <Button
                                        className="w-full"
                                        variant="secondary"
                                        onPress={handleBiometricUnlock}
                                        disabled={isUnlocking || isSigningIn}
                                    >
                                        {isUnlocking && <ActivityIndicator />}
                                        <SymbolView
                                            name={biometricSymbol}
                                            size={20}
                                            tintColor={
                                                THEME[scheme]
                                                    .secondaryForeground
                                            }
                                            fallback={
                                                <Icon
                                                    name={biometricFallbackIcon}
                                                    iconStyle="regular"
                                                    size={20}
                                                />
                                            }
                                        />
                                        <Text>
                                            {isUnlocking
                                                ? t("login.unlocking")
                                                : t("login.unlockWith", {
                                                      method: biometricLabel,
                                                  })}
                                        </Text>
                                    </Button>
                                )}
                            <Button
                                testID={TEST_IDS[TEST_IDS.LOGIN_SIGN_IN_BUTTON]}
                                className="w-full"
                                onPress={handleMicrosoftSignIn}
                                disabled={isSigningIn || isUnlocking}
                            >
                                {isSigningIn && <ActivityIndicator />}
                                <Text>
                                    {isSigningIn
                                        ? t("login.signingIn")
                                        : t("login.signInWithSchool")}
                                </Text>
                            </Button>
                            {/* <AppleSignInButton type={AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN} onError={setSignInError}/> */}
                        </CardContent>
                    </Card>
                    <AppStoreBadgeLink />
                </View>
            </View>
        </SafeAreaView>
    );
}
